import React, { useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material"
import CategoryIcon from "@mui/icons-material/Category"
import { buildCategoryTree, PRICE_GROUP_OPTIONS } from "constants/catalogData"
import { fetchPaginated } from "helpers/api_helper"
import { GET_PRODUCTS } from "helpers/api_urls"

const PG_CODES = new Set(PRICE_GROUP_OPTIONS.map(p => p.code))

/**
 * Decomposes a flat category array back into structured fields.
 */
const decompose = (arr, tree) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return { cat1: "", cat2: "", cat3: "", cat4: "", priceGroup: "", colorType: "" }
  }

  let cat1 = "", cat2 = "", cat3 = "", cat4 = "", priceGroup = "", colorType = ""

  if (arr[0] && tree[arr[0]]) {
    cat1 = arr[0]
  }

  if (cat1 && arr[1]) {
    const level2 = tree[cat1]
    if (level2 && level2[arr[1]] !== undefined) {
      cat2 = arr[1]
    }
  }

  if (cat1 && cat2 && arr[2]) {
    const level3 = tree[cat1][cat2]
    if (level3 && typeof level3 === "object" && !Array.isArray(level3)) {
      if (level3[arr[2]] !== undefined) {
        cat3 = arr[2]
      }
    } else if (Array.isArray(level3) && level3.includes(arr[2])) {
      cat3 = arr[2]
    }
  }

  if (cat1 && cat2 && cat3 && arr[3]) {
    const level3 = tree[cat1][cat2]
    if (level3 && typeof level3 === "object" && !Array.isArray(level3) && level3[cat3]) {
      const level4 = level3[cat3]
      if (Array.isArray(level4) && level4.includes(arr[3])) {
        cat4 = arr[3]
      }
    }
  }

  for (const val of arr) {
    if (PG_CODES.has(val)) {
      priceGroup = val
    }
  }

  const knownValues = new Set([cat1, cat2, cat3, cat4, priceGroup].filter(Boolean))
  for (const val of arr) {
    if (val && !knownValues.has(val)) {
      colorType = val
    }
  }

  return { cat1, cat2, cat3, cat4, priceGroup, colorType }
}

const selectSx = {
  fontSize: "0.85rem",
  borderRadius: 2,
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: 2 },
}

const StructuredCategoryEditor = ({ value, onChange }) => {
  const { t } = useTranslation()
  const [categoryTree, setCategoryTree] = useState({})
  const [treeLoading, setTreeLoading] = useState(true)
  const [fields, setFields] = useState({ cat1: "", cat2: "", cat3: "", cat4: "", priceGroup: "", colorType: "" })

  // Fetch products and build category tree on mount
  useEffect(() => {
    let cancelled = false
    const loadTree = async () => {
      try {
        const result = await fetchPaginated(GET_PRODUCTS, { limit: 5000, offset: 0 })
        if (!cancelled) {
          const tree = buildCategoryTree(result.data || [])
          setCategoryTree(tree)
          // Decompose value now that tree is available
          setFields(decompose(value, tree))
        }
      } catch (err) {
        console.error("Failed to build category tree:", err)
      } finally {
        if (!cancelled) setTreeLoading(false)
      }
    }
    loadTree()
    return () => { cancelled = true }
  }, [])

  // Sync from parent when value changes externally (e.g. product load)
  useEffect(() => {
    if (Object.keys(categoryTree).length > 0) {
      setFields(decompose(value, categoryTree))
    }
  }, [JSON.stringify(value), categoryTree])

  // Build array and notify parent whenever fields change
  const buildArray = (f) => {
    const arr = [f.cat1, f.cat2, f.cat3, f.cat4, f.priceGroup, f.colorType].filter(Boolean)
    onChange(arr)
  }

  const update = (patch) => {
    setFields(prev => {
      const next = { ...prev, ...patch }
      buildArray(next)
      return next
    })
  }

  // Derived options from dynamic tree
  const cat1Options = useMemo(() => Object.keys(categoryTree), [categoryTree])

  const cat2Options = useMemo(() => {
    if (!fields.cat1 || !categoryTree[fields.cat1]) return []
    return Object.keys(categoryTree[fields.cat1])
  }, [fields.cat1, categoryTree])

  const cat3Data = useMemo(() => {
    if (!fields.cat1 || !fields.cat2) return null
    const level = categoryTree[fields.cat1]?.[fields.cat2]
    if (!level) return null
    if (typeof level === "object" && !Array.isArray(level) && Object.keys(level).length > 0) {
      return { type: "object", options: Object.keys(level) }
    }
    if (Array.isArray(level) && level.length > 0) {
      return { type: "array", options: level }
    }
    return null
  }, [fields.cat1, fields.cat2, categoryTree])

  const cat4Options = useMemo(() => {
    if (!cat3Data || cat3Data.type !== "object" || !fields.cat3) return []
    const level = categoryTree[fields.cat1]?.[fields.cat2]?.[fields.cat3]
    if (Array.isArray(level)) return level
    if (typeof level === "object" && !Array.isArray(level)) return Object.keys(level)
    return []
  }, [fields.cat1, fields.cat2, fields.cat3, cat3Data, categoryTree])

  const showCat3 = !!cat3Data
  const showCat4 = cat3Data?.type === "object" && cat4Options.length > 0
  const showPriceGroup = fields.cat1 === "Cabinet"
  const showColorType = showPriceGroup && !!fields.priceGroup

  if (treeLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="#64748b">{t("Loading categories...")}</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="caption" sx={{ color: "#64748b", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
        <CategoryIcon sx={{ fontSize: 14 }} />
        {t("Categories")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {/* Cat1 */}
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ fontSize: "0.85rem" }}>{t("Category 1")}</InputLabel>
          <Select
            value={fields.cat1}
            label={t("Category 1")}
            onChange={(e) => update({ cat1: e.target.value, cat2: "", cat3: "", cat4: "", priceGroup: showPriceGroup ? "" : fields.priceGroup, colorType: "" })}
            sx={selectSx}
          >
            <MenuItem value=""><em>{t("Select...")}</em></MenuItem>
            {cat1Options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: "0.85rem" }}>{o}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Cat2 */}
        <FormControl size="small" fullWidth disabled={!fields.cat1}>
          <InputLabel sx={{ fontSize: "0.85rem" }}>{t("Category 2")}</InputLabel>
          <Select
            value={fields.cat2}
            label={t("Category 2")}
            onChange={(e) => update({ cat2: e.target.value, cat3: "", cat4: "" })}
            sx={selectSx}
          >
            <MenuItem value=""><em>{t("Select...")}</em></MenuItem>
            {cat2Options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: "0.85rem" }}>{o}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Cat3 */}
        {showCat3 && (
          <FormControl size="small" fullWidth disabled={!fields.cat2}>
            <InputLabel sx={{ fontSize: "0.85rem" }}>{t("Category 3")}</InputLabel>
            <Select
              value={fields.cat3}
              label={t("Category 3")}
              onChange={(e) => update({ cat3: e.target.value, cat4: "" })}
              sx={selectSx}
            >
              <MenuItem value=""><em>{t("Select...")}</em></MenuItem>
              {cat3Data.options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: "0.85rem" }}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {/* Cat4 */}
        {showCat4 && (
          <FormControl size="small" fullWidth disabled={!fields.cat3}>
            <InputLabel sx={{ fontSize: "0.85rem" }}>{t("Category 4")}</InputLabel>
            <Select
              value={fields.cat4}
              label={t("Category 4")}
              onChange={(e) => update({ cat4: e.target.value })}
              sx={selectSx}
            >
              <MenuItem value=""><em>{t("Select...")}</em></MenuItem>
              {cat4Options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: "0.85rem" }}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {/* Price Group */}
        {showPriceGroup && (
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: "0.85rem" }}>{t("Price Group")}</InputLabel>
            <Select
              value={fields.priceGroup}
              label={t("Price Group")}
              onChange={(e) => update({ priceGroup: e.target.value, colorType: e.target.value ? fields.colorType : "" })}
              sx={selectSx}
            >
              <MenuItem value=""><em>{t("Select...")}</em></MenuItem>
              {PRICE_GROUP_OPTIONS.map(pg => (
                <MenuItem key={pg.code} value={pg.code} sx={{ fontSize: "0.85rem" }}>
                  {pg.code} — {pg.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Color Type */}
        {showColorType && (
          <TextField
            size="small"
            fullWidth
            label={t("Color Type")}
            value={fields.colorType}
            onChange={(e) => update({ colorType: e.target.value })}
            placeholder="e.g. Unicolor, Wood, Oak..."
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
          />
        )}
      </Box>
    </Box>
  )
}

export default StructuredCategoryEditor
